import { EnabledProctoringOptions } from "@verity/api-client/types";
import {
	getProctoringOptionDisabledDetails,
	isAnyOnValueEnabledForProctoringOption,
	isOptionSupportedForPlatform,
	isPlatformSelected,
	PROCTORING_OPTION_INFO,
} from "@verity/proctoring-option";
import { OptionRelatedInstituteSettings, ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { useCallback, useMemo } from "react";
import { EditQuizType, VerityInstituteSettings } from "../../../types/common";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import { checkIfVerityAssessment, checkIfStandaloneAssessment } from "../../editQuizHelper";
import {
	CANDIDATE_ONLY_OPTIONS,
	getOptionsInCategory,
	PROCTORING_OPTION_CATEGORIES,
} from "../option-text/ProctoringOptionText";
/**
 * Hook that wraps the proctoring option methods. So we don't keep passing the current state everytime
 */
export const useProctoringHelpers = (overrides?: {
	verityInstituteSettings?: VerityInstituteSettings;
	enabledProctoringOptions?: EnabledProctoringOptions;
	isThirdPartyEnabled?: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	proctoringOption?: Partial<ProctoringOption>;
	quizType?: EditQuizType;
}) => {
	const context = useProctoringOptionContext();
	const verityInstituteSettings = overrides?.verityInstituteSettings ?? context.verityInstituteSettings;
	const enabledProctoringOptions = overrides?.enabledProctoringOptions ?? context.enabledProctoringOptions;
	const isThirdPartyEnabled = overrides?.isThirdPartyEnabled ?? context.isThirdPartyEnabled;
	const optionRelatedInstituteSettings =
		overrides?.optionRelatedInstituteSettings ?? context.optionRelatedInstituteSettings;
	const proctoringOption = overrides?.proctoringOption ?? context.proctoringOption;
	const quizType = overrides?.quizType ?? context.quizType;

	/**
	 * Given a proctoring option, returns if we should even show the option in the UI
	 */
	const shouldShowOption = useCallback(
		(option: keyof ProctoringOption) => {
			const optionInfo = PROCTORING_OPTION_INFO[option];
			const onValues = optionInfo?.isList ? [[""]] : (optionInfo?.onValues ?? []);

			const application = localStorage.getItem("app");

			if (application !== "candidate" && CANDIDATE_ONLY_OPTIONS.includes(option)) return false;

			if (option === "uploadScratchNotes" && quizType === "verityAssignment") return false;

			if (
				option === "enableManualEndProctoring" &&
				(!proctoringOption.isLockdown ||
					checkIfVerityAssessment(quizType) ||
					checkIfStandaloneAssessment(quizType) ||
					isThirdPartyEnabled)
			) {
				return false;
			}

			return onValues.some((onValue) => {
				const disabledDetails = getProctoringOptionDisabledDetails(
					option,
					onValue,
					proctoringOption,
					verityInstituteSettings,
					optionRelatedInstituteSettings,
					enabledProctoringOptions,
				);
				return !Object.keys(disabledDetails.disabledReasons).some(
					(reason) =>
						reason === "platformUnsupported" ||
						reason === "liteMode" ||
						reason === "platformNotAllowed" ||
						reason === "instituteDisabledOption" ||
						reason === "verityDisabledOption",
				);
			});
		},
		[
			enabledProctoringOptions,
			isThirdPartyEnabled,
			optionRelatedInstituteSettings,
			proctoringOption,
			quizType,
			verityInstituteSettings,
		],
	);

	/**
	 * Given a proctoring option, returns if the proctoring option is allowed based on current settings
	 */
	const isOptionAllowed = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) => {
			return value === undefined
				? isAnyOnValueEnabledForProctoringOption(
						option,
						proctoringOption,
						verityInstituteSettings,
						optionRelatedInstituteSettings,
						enabledProctoringOptions,
					)
				: !getProctoringOptionDisabledDetails(
						option,
						value,
						proctoringOption,
						verityInstituteSettings,
						optionRelatedInstituteSettings,
						enabledProctoringOptions,
					).disabled;
		},
		[verityInstituteSettings, enabledProctoringOptions, optionRelatedInstituteSettings, proctoringOption],
	);

	/**
	 *  Given an option, returns the list of selected platforms that are supported for that option.
	 *  If the option is supported for all selected platforms, returns an empty set.
	 */
	const getPartiallySupportedPlatforms = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]): Set<keyof ProctoringPlatforms> => {
			const optionInfo = PROCTORING_OPTION_INFO[option];

			const valuesToCheck =
				value !== undefined
					? [value]
					: optionInfo?.isList
						? [[""] as ProctoringOption[O]]
						: (optionInfo?.onValues ?? []);

			const supportedPlatforms = new Set<keyof ProctoringPlatforms>();
			if (!proctoringOption) return new Set();
			const selectedPlatforms = (
				["isExtension", "isLockdown", "isMobile", "isChromeOs", "isBrowser"] as (keyof ProctoringPlatforms)[]
			).filter((platform) => isPlatformSelected(proctoringOption, platform));

			selectedPlatforms.forEach((platform) => {
				const isSupported = valuesToCheck.some((v) => isOptionSupportedForPlatform(platform, option, v));
				if (isSupported) {
					supportedPlatforms.add(platform);
				}
			});

			// If all selected platforms are supported, return empty
			if (supportedPlatforms.size === selectedPlatforms.length) return new Set();
			return new Set(supportedPlatforms);
		},
		[proctoringOption],
	);

	const allowedCategories = useMemo(() => {
		return PROCTORING_OPTION_CATEGORIES.filter((category) => getOptionsInCategory(category).some(shouldShowOption));
	}, [shouldShowOption]);

	return {
		isOptionAllowed,
		getPartiallySupportedPlatforms,
		shouldShowOption,
		allowedCategories,
	};
};
